"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type FlowNodeTone =
  | "event"
  | "debit"
  | "credit"
  | "neutral"
  | "waiting"
  | "bank";

function shortLabel(text: string, max = 32): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

export function FlowDiagramFrame({
  title,
  caption,
  children,
  className,
}: {
  title?: string;
  caption?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-gradient-to-b from-muted/30 to-background p-4",
        className
      )}
    >
      {title ? (
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </p>
      ) : null}
      <div className="overflow-x-auto">{children}</div>
      {caption ? (
        <p className="mt-3 text-xs text-muted-foreground">{caption}</p>
      ) : null}
    </div>
  );
}

const toneStyles: Record<FlowNodeTone, string> = {
  event:
    "border-blue-300 bg-blue-50 text-blue-950 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-100",
  debit:
    "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100",
  credit:
    "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100",
  neutral: "border-border bg-card text-foreground",
  waiting:
    "border-dashed border-muted-foreground/40 bg-muted/20 text-muted-foreground",
  bank: "border-violet-300 bg-violet-50 text-violet-950 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100",
};

export function FlowNode({
  label,
  sublabel,
  amount,
  tone = "neutral",
  className,
}: {
  label: string;
  sublabel?: string;
  amount?: string;
  tone?: FlowNodeTone;
  className?: string;
}) {
  const displayLabel = shortLabel(label);
  return (
    <div
      className={cn(
        "min-w-[9rem] max-w-[14rem] rounded-lg border px-3 py-2 text-center shadow-sm",
        toneStyles[tone],
        className
      )}
      title={label !== displayLabel ? label : undefined}
    >
      <div className="text-xs font-semibold leading-snug">{displayLabel}</div>
      {sublabel ? (
        <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-80">
          {sublabel}
        </div>
      ) : null}
      {amount ? (
        <div className="mt-1 font-mono text-xs tabular-nums font-medium">
          {amount}
        </div>
      ) : null}
    </div>
  );
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      className={cn("h-5 w-8 shrink-0 text-muted-foreground/70", className)}
      viewBox="0 0 32 20"
      aria-hidden
    >
      <path
        d="M2 10h22M20 5l8 5-8 5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ArrowDown({ className }: { className?: string }) {
  return (
    <svg
      className={cn("h-8 w-5 shrink-0 text-muted-foreground/70", className)}
      viewBox="0 0 20 32"
      aria-hidden
    >
      <path
        d="M10 2v22M5 18l5 8 5-8"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function HorizontalFlow({
  steps,
}: {
  steps: Array<{
    label: string;
    sublabel?: string;
    amount?: string;
    tone?: FlowNodeTone;
  }>;
}) {
  return (
    <div className="flex min-w-max items-center justify-center gap-1 py-2">
      {steps.map((step, index) => (
        <div key={`${step.label}-${index}`} className="flex items-center gap-1">
          <FlowNode {...step} />
          {index < steps.length - 1 ? <ArrowRight /> : null}
        </div>
      ))}
    </div>
  );
}

export function VerticalFlow({
  steps,
}: {
  steps: Array<{
    label: string;
    sublabel?: string;
    amount?: string;
    tone?: FlowNodeTone;
  }>;
}) {
  return (
    <div className="flex flex-col items-center gap-1 py-2">
      {steps.map((step, index) => (
        <div key={`${step.label}-${index}`} className="flex flex-col items-center">
          <FlowNode {...step} />
          {index < steps.length - 1 ? <ArrowDown /> : null}
        </div>
      ))}
    </div>
  );
}

export function AccrualFlowDiagram({
  eventLabel,
  debitLabel,
  creditLabel,
  amount,
  currency,
}: {
  eventLabel: string;
  debitLabel: string;
  creditLabel: string;
  amount: string;
  currency: string;
}) {
  const formatted = `${amount} ${currency}`;
  return (
    <FlowDiagramFrame title="Money flow">
      <div className="flex min-w-max flex-col items-center gap-2 py-2">
        <FlowNode label={eventLabel} tone="event" />
        <ArrowDown />
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-1">
            <FlowNode
              label={debitLabel}
              sublabel="Debit"
              amount={formatted}
              tone="debit"
            />
          </div>
          <div className="flex items-center self-center text-xs text-muted-foreground">
            balances
          </div>
          <div className="flex flex-col items-center gap-1">
            <FlowNode
              label={creditLabel}
              sublabel="Credit"
              amount={formatted}
              tone="credit"
            />
          </div>
        </div>
      </div>
    </FlowDiagramFrame>
  );
}

export function BrandSetupDiagram({
  brandName,
  arLabel,
  revenueLabel,
  currency,
}: {
  brandName: string;
  arLabel: string;
  revenueLabel: string;
  currency: string;
}) {
  return (
    <FlowDiagramFrame
      title="Ledgers created at setup"
      caption="No journals yet — accounts are ready for invoices and payments."
    >
      <div className="flex min-w-max flex-col items-center gap-2 py-2">
        <FlowNode label={`Brand: ${brandName}`} sublabel="provisioned" tone="event" />
        <ArrowDown />
        <div className="flex flex-wrap items-center justify-center gap-3">
          <FlowNode
            label={arLabel}
            sublabel="Accounts receivable"
            tone="debit"
          />
          <FlowNode
            label={revenueLabel}
            sublabel="Sales revenue"
            tone="credit"
          />
        </div>
        <p className="text-xs text-muted-foreground">Currency: {currency}</p>
      </div>
    </FlowDiagramFrame>
  );
}

export function CustomerPmSetupDiagram({
  pmLabel,
  receivingLabel,
  pdqLabel,
  clearingLabel,
  bankLabel,
  hasBank,
}: {
  pmLabel: string;
  receivingLabel: string;
  pdqLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  hasBank: boolean;
}) {
  return (
    <FlowDiagramFrame title="Payment method ledger funnel">
      <VerticalFlow
        steps={[
          { label: pmLabel, sublabel: "payment method", tone: "event" },
          { label: receivingLabel, sublabel: "receiving", tone: "debit" },
          { label: pdqLabel, sublabel: "PDQ fees", tone: "neutral" },
          { label: clearingLabel, sublabel: "clearing", tone: "neutral" },
          ...(hasBank && bankLabel
            ? [{ label: bankLabel, sublabel: "connected bank", tone: "bank" as const }]
            : [
                {
                  label: "No bank connected",
                  sublabel: "stops at clearing",
                  tone: "waiting" as const,
                },
              ]),
        ]}
      />
    </FlowDiagramFrame>
  );
}

export function CustomerPaymentAutoDiagram({
  receivingLabel,
  arLabel,
  pdqLabel,
  clearingLabel,
  bankLabel,
  gross,
  fee,
  net,
  currency,
  hasPdq,
  hasBank,
}: {
  receivingLabel: string;
  arLabel: string;
  pdqLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  gross: string;
  fee: string;
  net: string;
  currency: string;
  hasPdq: boolean;
  hasBank: boolean;
}) {
  return (
    <FlowDiagramFrame
      title="Customer payment — money path"
      caption="Gross lands in receiving, allocates to AR, then fees and net move through clearing to bank."
    >
      <div className="flex min-w-[28rem] flex-col items-center gap-2 py-2">
        <FlowNode
          label="Customer pays"
          amount={`${gross} ${currency}`}
          tone="event"
        />
        <ArrowDown />
        <FlowNode
          label={receivingLabel}
          sublabel="DR gross"
          amount={`${gross} ${currency}`}
          tone="debit"
        />
        <ArrowDown />
        <div className="flex flex-wrap items-start justify-center gap-6">
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] uppercase text-muted-foreground">
              CR to AR
            </span>
            <FlowNode
              label={arLabel}
              amount={`${gross} ${currency}`}
              tone="credit"
            />
          </div>
          {hasPdq ? (
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] uppercase text-muted-foreground">
                PDQ fee
              </span>
              <FlowNode
                label={pdqLabel}
                amount={`${fee} ${currency}`}
                tone="neutral"
              />
            </div>
          ) : null}
        </div>
        <ArrowDown />
        <FlowNode
          label={clearingLabel}
          sublabel="net staging"
          amount={`${hasPdq ? net : gross} ${currency}`}
          tone="neutral"
        />
        {hasBank && bankLabel ? (
          <>
            <ArrowDown />
            <FlowNode
              label={bankLabel}
              sublabel="settled"
              amount={`${hasPdq ? net : gross} ${currency}`}
              tone="bank"
            />
          </>
        ) : (
          <>
            <ArrowDown />
            <FlowNode label="Stops at clearing" tone="waiting" />
          </>
        )}
      </div>
    </FlowDiagramFrame>
  );
}

export function CustomerPaymentPdqPendingDiagram({
  receivingLabel,
  arLabel,
  pdqLabel,
  clearingLabel,
  bankLabel,
  gross,
  net,
  fee,
  currency,
  hasBank,
}: {
  receivingLabel: string;
  arLabel: string;
  pdqLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  gross: string;
  net: string;
  fee: string;
  currency: string;
  hasBank: boolean;
}) {
  return (
    <FlowDiagramFrame
      title="PDQ reconcile path — two phases"
      caption="Phase 1 on receipt. Phase 2 when accountant runs PDQ batch reconcile."
    >
      <div className="grid min-w-[32rem] gap-6 md:grid-cols-2">
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-3">
          <p className="text-xs font-medium text-muted-foreground">
            Phase 1 — on receipt
          </p>
          <FlowNode label="Customer pays" amount={`${gross} ${currency}`} tone="event" />
          <ArrowDown />
          <FlowNode label={receivingLabel} tone="debit" amount={`${gross} ${currency}`} />
          <ArrowDown />
          <FlowNode label={arLabel} tone="credit" amount={`${gross} ${currency}`} />
        </div>
        <div className="flex flex-col items-center gap-2 rounded-lg border p-3">
          <p className="text-xs font-medium text-muted-foreground">
            Phase 2 — PDQ reconcile
          </p>
          <FlowNode label="PDQ batch reconcile" tone="event" />
          <ArrowDown />
          <div className="flex flex-wrap justify-center gap-2">
            <FlowNode label={pdqLabel} amount={`${fee} ${currency}`} tone="neutral" />
            <FlowNode label={clearingLabel} amount={`${net} ${currency}`} tone="neutral" />
          </div>
          {hasBank && bankLabel ? (
            <>
              <ArrowDown />
              <FlowNode label={bankLabel} amount={`${net} ${currency}`} tone="bank" />
            </>
          ) : (
            <FlowNode label="Stops at clearing" tone="waiting" className="mt-2" />
          )}
        </div>
      </div>
    </FlowDiagramFrame>
  );
}

export function SupplierPmSetupDiagram({
  pmLabel,
  clearingLabel,
  bankLabel,
  hasBank,
}: {
  pmLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  hasBank: boolean;
}) {
  return (
    <FlowDiagramFrame title="Supplier payment method ledger path">
      <VerticalFlow
        steps={[
          { label: pmLabel, sublabel: "payment method", tone: "event" },
          { label: clearingLabel, sublabel: "clearing", tone: "neutral" },
          ...(hasBank && bankLabel
            ? [{ label: bankLabel, sublabel: "connected bank", tone: "bank" as const }]
            : [{ label: "No bank connected", tone: "waiting" as const }]),
        ]}
      />
    </FlowDiagramFrame>
  );
}

export function SupplierPaymentFlowDiagram({
  payableLabel,
  clearingLabel,
  bankLabel,
  amount,
  currency,
  hasBank,
}: {
  payableLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  amount: string;
  currency: string;
  hasBank: boolean;
}) {
  const formatted = `${amount} ${currency}`;
  return (
    <FlowDiagramFrame title="Supplier payment — money path">
      <VerticalFlow
        steps={[
          {
            label: "Supplier payment recorded",
            amount: formatted,
            tone: "event",
          },
          {
            label: payableLabel,
            sublabel: "DR payable",
            amount: formatted,
            tone: "debit",
          },
          {
            label: clearingLabel,
            sublabel: "CR clearing",
            amount: formatted,
            tone: "neutral",
          },
          ...(hasBank && bankLabel
            ? [
                {
                  label: bankLabel,
                  sublabel: "CR bank",
                  amount: formatted,
                  tone: "bank" as const,
                },
              ]
            : [
                {
                  label: "Stops at clearing",
                  tone: "waiting" as const,
                },
              ]),
        ]}
      />
    </FlowDiagramFrame>
  );
}

export function ReversalFlowDiagram({
  originalDebitLabel,
  originalCreditLabel,
  amount,
  currency,
}: {
  originalDebitLabel: string;
  originalCreditLabel: string;
  amount: string;
  currency: string;
}) {
  const formatted = `${amount} ${currency}`;
  return (
    <FlowDiagramFrame
      title="Void = reversal journals"
      caption="Each original journal gets an opposite entry. Amounts stay the same; DR and CR swap."
    >
      <div className="grid min-w-[28rem] gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-center text-xs font-medium text-muted-foreground">
            Original
          </p>
          <div className="flex justify-center gap-2">
            <FlowNode
              label={originalDebitLabel}
              sublabel="DR"
              amount={formatted}
              tone="debit"
            />
            <FlowNode
              label={originalCreditLabel}
              sublabel="CR"
              amount={formatted}
              tone="credit"
            />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-center text-xs font-medium text-muted-foreground">
            Reversal
          </p>
          <div className="flex justify-center gap-2">
            <FlowNode
              label={originalDebitLabel}
              sublabel="CR"
              amount={formatted}
              tone="credit"
            />
            <FlowNode
              label={originalCreditLabel}
              sublabel="DR"
              amount={formatted}
              tone="debit"
            />
          </div>
        </div>
      </div>
    </FlowDiagramFrame>
  );
}

export type CustomerPmSummaryEntry = {
  id: string;
  label: string;
  currency?: string;
  receivingLabel: string;
  pdqLabel: string;
  clearingLabel: string;
  bankLabel?: string;
  hasBank: boolean;
  archived?: boolean;
};

export function CustomerPaymentMethodsSummaryDiagram({
  entries,
}: {
  entries: CustomerPmSummaryEntry[];
}) {
  return (
    <FlowDiagramFrame
      title="Payment methods → ledgers"
      caption="Each row shows how customer payments flow through system ledgers to an optional connected bank account."
    >
      <div className="space-y-3">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className={cn(
              "rounded-md border bg-background/50 p-3",
              entry.archived && "opacity-60"
            )}
          >
            <HorizontalFlow
              steps={[
                {
                  label: entry.label,
                  sublabel: entry.archived
                    ? "archived"
                    : entry.currency ?? "payment method",
                  tone: "event",
                },
                {
                  label: entry.receivingLabel,
                  sublabel: "receiving",
                  tone: "debit",
                },
                {
                  label: entry.pdqLabel,
                  sublabel: "PDQ fees",
                  tone: "neutral",
                },
                {
                  label: entry.clearingLabel,
                  sublabel: "clearing",
                  tone: "neutral",
                },
                ...(entry.hasBank && entry.bankLabel
                  ? [
                      {
                        label: entry.bankLabel,
                        sublabel: "connected bank",
                        tone: "bank" as const,
                      },
                    ]
                  : [
                      {
                        label: "No bank connected",
                        sublabel: "stops at clearing",
                        tone: "waiting" as const,
                      },
                    ]),
              ]}
            />
          </div>
        ))}
      </div>
    </FlowDiagramFrame>
  );
}

export function CurrencyFlowDiagram({
  originalAmount,
  originalCurrency,
  ledgerAmount,
  ledgerCurrency,
  fxRate,
}: {
  originalAmount: string;
  originalCurrency: string;
  ledgerAmount: string;
  ledgerCurrency: string;
  fxRate: string;
}) {
  return (
    <FlowDiagramFrame title="Foreign payment converted to ledger currency">
      <HorizontalFlow
        steps={[
          {
            label: `Receipt: ${originalAmount} ${originalCurrency}`,
            tone: "event",
          },
          {
            label: `FX @ ${fxRate}`,
            sublabel: "conversion",
            tone: "waiting",
          },
          {
            label: `Ledger leg: ${ledgerAmount} ${ledgerCurrency}`,
            sublabel: "stored on journal leg",
            tone: "neutral",
          },
        ]}
      />
    </FlowDiagramFrame>
  );
}

export function StripeAutomaticPdqFlowDiagram() {
  return (
    <FlowDiagramFrame
      title="Stripe auto PDQ flow"
      caption="The fee amount comes from Stripe's balance transaction, converted to the brand home currency when needed."
    >
      <HorizontalFlow
        steps={[
          {
            label: "Stripe Checkout payment",
            sublabel: "booking or quote deposit",
            tone: "event",
          },
          {
            label: "Webhook creates receipt",
            sublabel: "linked payment method",
            tone: "event",
          },
          {
            label: "Stripe balance transaction fee",
            sublabel: "actual processing cost",
            tone: "neutral",
          },
          {
            label: "PDQ fee journal",
            sublabel: "posted with receipt",
            tone: "debit",
          },
        ]}
      />
    </FlowDiagramFrame>
  );
}

export function CustomerCreditVoucherIssuanceFlowDiagram({
  arLabel,
  receivingLabel,
  liabilityLabel,
  amount,
  currency,
}: {
  arLabel: string;
  receivingLabel: string;
  liabilityLabel: string;
  amount: string;
  currency: string;
}) {
  const formatted = `${amount} ${currency}`;
  return (
    <FlowDiagramFrame
      title="Issuance — refund as store credit"
      caption="No cash leaves the bank. AR on the cancelled booking is relieved and customer store-credit liability is created."
    >
      <div className="space-y-4">
        <AccrualFlowDiagram
          eventLabel="Refund issued as credit voucher"
          debitLabel={receivingLabel}
          creditLabel={arLabel}
          amount={amount}
          currency={currency}
        />
        <AccrualFlowDiagram
          eventLabel="Shift to store-credit liability"
          debitLabel={receivingLabel}
          creditLabel={liabilityLabel}
          amount={amount}
          currency={currency}
        />
        <HorizontalFlow
          steps={[
            {
              label: arLabel,
              sublabel: "AR relieved",
              amount: formatted,
              tone: "credit",
            },
            {
              label: liabilityLabel,
              sublabel: "liability created",
              amount: formatted,
              tone: "credit",
            },
            {
              label: "Bank accounts",
              sublabel: "no movement",
              tone: "waiting",
            },
          ]}
        />
      </div>
    </FlowDiagramFrame>
  );
}

export function CustomerCreditVoucherRedemptionFlowDiagram({
  arLabel,
  liabilityLabel,
  amount,
  currency,
}: {
  arLabel: string;
  liabilityLabel: string;
  amount: string;
  currency: string;
}) {
  const formatted = `${amount} ${currency}`;
  return (
    <FlowDiagramFrame
      title="Redemption — pay with store credit"
      caption="Customer liability decreases and AR on the new booking is settled. No bank, PDQ, or clearing journals."
    >
      <div className="space-y-4">
        <AccrualFlowDiagram
          eventLabel="Voucher applied to booking payment"
          debitLabel={liabilityLabel}
          creditLabel={arLabel}
          amount={amount}
          currency={currency}
        />
        <HorizontalFlow
          steps={[
            {
              label: liabilityLabel,
              sublabel: "liability reduced",
              amount: formatted,
              tone: "debit",
            },
            {
              label: arLabel,
              sublabel: "invoice settled",
              amount: formatted,
              tone: "credit",
            },
            {
              label: "Bank accounts",
              sublabel: "no movement",
              tone: "waiting",
            },
          ]}
        />
      </div>
    </FlowDiagramFrame>
  );
}
